import { Helmet } from "react-helmet-async";

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  type?: "website" | "article";
  noindex?: boolean;
  preloadImage?: string;
  jsonLd?: object | object[];
}

const SEO = ({ 
  title, 
  description, 
  canonical,
  type = "website",
  noindex = false,
  preloadImage,
  jsonLd
}: SEOProps) => {
  const siteTitle = "AurØra – Global Property Income";
  const fullTitle = title === siteTitle || title.includes("AurØra") ? title : `${title} | AurØra`;
  const baseUrl = "https://aur0ra.com";
  const canonicalUrl = canonical ? `${baseUrl}${canonical}` : undefined;
  const ogImage = `${baseUrl}/og-image.png`;

  // Only include page-specific JSON-LD (Organization/WebSite schemas are in index.html)
  const pageSchemas = jsonLd 
    ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd])
    : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Preload LCP image if specified */}
      {preloadImage && (
        <link rel="preload" as="image" href={preloadImage} />
      )}
      
      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:image" content={ogImage} />
      
      {/* Twitter */}
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Page-specific JSON-LD only (avoid duplicating static schemas from index.html) */}
      {pageSchemas.map((schema, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
    </Helmet>
  );
};

export default SEO;
